import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageListContainer, PageCreateButton } from "../../../components/Page";
import { useConfirm } from "../../../components/feedback";
import DataTableWithPagination from "../../../components/DataTable";
import ProjectDialog from "../../../components/GenericDialog";
import { ANOMALY_EVENT_SEARCH_FIELDS } from "../../../lib/anomaly/constants";
import { getAnomalyEventFormValidationError, mapAnomalyEventFormToPayload } from "../../../lib/anomaly/form";
import { buildAnomalyEventListFilter } from "../../../lib/anomaly/listFilter";
import { anomalyGuideService } from "../../../services/anomalyGuideService";
import { anomalyService } from "../../../services/anomalyService";
import { customerService, type Customer } from "../../../services/customerService";
import CustomerSearchAutocomplete from "../../../components/customers/CustomerSearchAutocomplete";
import { getCurrentUserRoles, isAdmin } from "../../../utils/authorization";
import AnomalyModel, { getAnomalyCols } from "../model";
import AnomalyCategoryFilters from "./components/AnomalyCategoryFilters";
import AnomalyEventDialogBody from "./components/AnomalyEventDialogBody";

function AnomaliesPage() {
  const { t } = useTranslation();
  const datatable = useRef<any>(null);
  const createDialogRef = useRef<any>(null);
  const editDialogRef = useRef<any>(null);
  const confirm = useConfirm();
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerNameById, setCustomerNameById] = useState<Record<string, string>>({});
  const [taxonomy, setTaxonomy] = useState<Array<{ category: string; subCategory: string }>>([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);

  useEffect(() => {
    void getCurrentUserRoles().then((roles) => setIsAdminUser(isAdmin(roles)));
  }, []);

  useEffect(() => {
    if (!isAdminUser) {
      setCustomerNameById({});
      return;
    }

    let active = true;

    void customerService
      .getCustomers(0, 500, undefined, ["id", "name"])
      .then((response) => {
        if (!active) {
          return;
        }

        const next: Record<string, string> = {};
        for (const customer of response?.content ?? []) {
          const id = customer?.id?.trim();
          if (!id) {
            continue;
          }
          next[id] = customer.name?.trim() || id;
        }
        setCustomerNameById(next);
      })
      .catch((error) => {
        console.error("Failed to load customers for anomaly table:", error);
        if (active) {
          setCustomerNameById({});
        }
      });

    return () => {
      active = false;
    };
  }, [isAdminUser]);

  useEffect(() => {
    let active = true;
    setTaxonomyLoading(true);

    void anomalyGuideService
      .listTaxonomy()
      .then((items) => {
        if (active) {
          setTaxonomy(items);
        }
      })
      .catch((error) => {
        console.error("Failed to load anomaly guide categories:", error);
        if (active) {
          setTaxonomy([]);
        }
      })
      .finally(() => {
        if (active) {
          setTaxonomyLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const categoryOptions = useMemo(() => {
    return Array.from(new Set(taxonomy.map((item) => item.category))).sort((a, b) => a.localeCompare(b));
  }, [taxonomy]);

  const subCategoryOptions = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    return Array.from(
      new Set(
        taxonomy
          .filter((item) => item.category === selectedCategory)
          .map((item) => item.subCategory)
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [taxonomy, selectedCategory]);

  const listFilterParam = useMemo(
    () =>
      buildAnomalyEventListFilter({
        customerId: isAdminUser ? selectedCustomer?.id : undefined,
        category: selectedCategory,
        subCategory: selectedSubCategory,
      }),
    [isAdminUser, selectedCustomer?.id, selectedCategory, selectedSubCategory],
  );

  const anomalyCols = useMemo(() => getAnomalyCols(t), [t]);

  const tableCols = useMemo(() => {
    if (!isAdminUser) {
      return anomalyCols;
    }

    return [
      {
        name: t("anomalies.cols.customer"),
        field: "customerId",
        sortable: true,
        selector: (row: { customerId?: string; customer?: { name?: string } }) => {
          const nestedName = row.customer?.name?.trim();
          if (nestedName) {
            return nestedName;
          }
          const customerId = row.customerId?.trim();
          if (!customerId) {
            return "";
          }
          return customerNameById[customerId] || customerId;
        },
      },
      ...anomalyCols,
    ];
  }, [isAdminUser, customerNameById, anomalyCols, t]);

  const handleClearFilters = () => {
    setSelectedCustomer(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
  };

  const handleCategoryChange = (value: string | null) => {
    setSelectedCategory(value);
    setSelectedSubCategory(null);
  };

  useEffect(() => {
    const handleGlobalRefresh = () => {
      datatable.current?.refresh?.();
    };

    window.addEventListener("refreshDataTable", handleGlobalRefresh);
    return () => window.removeEventListener("refreshDataTable", handleGlobalRefresh);
  }, []);

  const showSnack = (opts: unknown) => {
    window.dispatchEvent(new CustomEvent("showGlobalNotification", { detail: opts }));
  };

  const showValidationError = (message: string) => {
    AnomalyModel.message(AnomalyModel.ERROR, t("common.validationError"), message, Date.now()).then((opts) =>
      showSnack(opts),
    );
  };

  const validateForm = (formJson: Record<string, string>) => {
    const validationError = getAnomalyEventFormValidationError(formJson, {
      requireCustomer: isAdminUser,
      customer: {
        startWorkTime: formJson.customerStartWorkTime,
        endWorkTime: formJson.customerEndWorkTime,
        timeZone: formJson.customerTimeZone,
      },
    });
    if (validationError) {
      showValidationError(validationError);
      return false;
    }

    return true;
  };

  const openCreateDialog = () => {
    createDialogRef.current?.showDialog(AnomalyModel.createDialogOpts(t));
  };

  const createSubmitAction = (formJson: Record<string, string>) => {
    if (!validateForm(formJson)) {
      return false;
    }

    anomalyService
      .createAnomalyEvent(mapAnomalyEventFormToPayload(formJson))
      .then(() =>
        AnomalyModel.message(
          AnomalyModel.SUCCESS,
          t("common.createRequest"),
          t("anomalies.createdSuccess"),
          Date.now(),
        ),
      )
      .then((opts) => {
        showSnack(opts);
        setTimeout(() => datatable.current?.refresh?.(), 500);
      })
      .catch((error: unknown) => {
        console.error(error);
      });

    return undefined;
  };

  const editSubmitAction = (formJson: Record<string, string>) => {
    if (!validateForm(formJson)) {
      return false;
    }

    anomalyService
      .updateAnomalyEvent({
        id: formJson.id,
        ...mapAnomalyEventFormToPayload(formJson),
      })
      .then(() =>
        AnomalyModel.message(
          AnomalyModel.SUCCESS,
          t("common.updateRequest"),
          t("anomalies.updatedSuccess"),
          Date.now(),
        ),
      )
      .then((opts) => {
        showSnack(opts);
        setTimeout(() => datatable.current?.refresh?.(), 500);
      })
      .catch((error: unknown) => {
        console.error(error);
      });

    return undefined;
  };

  const handleDelete = async (item: { id?: string; category?: string; subCategory?: string }) => {
    return new Promise((resolve) => {
      confirm({
        description: t("anomalies.deleteConfirm", {
          id: `${item.category || ""} / ${item.subCategory || item.id}`,
        }),
      })
        .then(() => anomalyService.deleteAnomalyEvent(String(item.id)))
        .then(() => resolve({}))
        .then(() =>
          AnomalyModel.message(
            AnomalyModel.SUCCESS,
            t("common.deleteRequest"),
            t("anomalies.deletedSuccess"),
            Date.now(),
          ),
        )
        .then((opts) => {
          showSnack(opts);
          setTimeout(() => datatable.current?.refresh?.(), 500);
        })
        .catch((error: unknown) => {
          console.error(error);
        });
    });
  };

  const handleEdit = async (item: Record<string, unknown>) => {
    editDialogRef.current?.showDialog(AnomalyModel.editDialogOpts(t), item);
  };

  return (
    <PageListContainer>
      <ProjectDialog
        ref={createDialogRef}
        submitAction={createSubmitAction}
        body={AnomalyEventDialogBody}
        maxWidth="md"
        fullWidth
      />
      <ProjectDialog
        ref={editDialogRef}
        submitAction={editSubmitAction}
        body={AnomalyEventDialogBody}
        maxWidth="md"
        fullWidth
      />

      <DataTableWithPagination
        ref={datatable}
        cols={tableCols}
        extraListFields={isAdminUser ? ["customerId"] : undefined}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        headerActions={
          <PageCreateButton onClick={openCreateDialog}>{t("anomalies.newEvent")}</PageCreateButton>
        }
        fetchUrl="/anomaly-event/read"
        additionalParameters={listFilterParam}
        searchKey="category"
        searchFields={ANOMALY_EVENT_SEARCH_FIELDS}
        searchPlaceholder={t("anomalies.searchPlaceholder")}
        extraFilterCount={
          (isAdminUser && selectedCustomer ? 1 : 0) +
          (selectedCategory ? 1 : 0) +
          (selectedSubCategory ? 1 : 0)
        }
        onClearExtraFilters={handleClearFilters}
        extraFilters={
          <>
            {isAdminUser ? (
              <CustomerSearchAutocomplete
                value={selectedCustomer}
                onChange={setSelectedCustomer}
                label={t("anomalies.searchCustomer")}
                placeholder={t("common.typeAtLeast2")}
              />
            ) : null}
            <AnomalyCategoryFilters
              categoryOptions={categoryOptions}
              subCategoryOptions={subCategoryOptions}
              selectedCategory={selectedCategory}
              selectedSubCategory={selectedSubCategory}
              onCategoryChange={handleCategoryChange}
              onSubCategoryChange={setSelectedSubCategory}
              loading={taxonomyLoading}
            />
          </>
        }
      />
    </PageListContainer>
  );
}

export default AnomaliesPage;
